import { CustomColumnProps, EMPTY_CELL_VALUE, FieldValue } from '@msdk/definitions'
import { Chip, createComponentWrapperWithRef, OverflowList, styled, Tooltip, Typography } from '@msdk/ui'
import React, { useEffect, useRef, useState } from 'react'

const StyledBaseChip = styled(Chip, { shouldForwardProp: (props) => props != 'cursor' })<{ cursor: boolean }>`
  ${({ cursor }) => (cursor ? 'cursor: pointer;' : '')};
`

const StyledChip = createComponentWrapperWithRef(
  {
    mui: StyledBaseChip,
    nova: StyledBaseChip,
  },
  'Chip',
)

const More = styled(Typography, { shouldForwardProp: (props) => props != 'cursor' })`
  cursor: pointer;
  padding-left: 4px;
  white-space: nowrap;
`

const ChipLabel = styled('div')`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const UnorderedList = styled('ul')`
  margin: 0;
  padding: 8px;
  list-style-position: inside;
  max-height: 300px;
  overflow: auto;
`

const ListItem = styled('li')``

const OverflowChip = ({ label, onClick }: { label: string; onClick?: () => void }) => {
  const textRef = useRef<HTMLDivElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)
  const [isHover, setIsOver] = useState(false)

  useEffect(() => {
    if (!textRef.current) {
      return
    }
    setIsTruncated(textRef.current.clientWidth < textRef.current.scrollWidth)
  }, [isHover])

  return (
    <Tooltip title={label} open={isTruncated && isHover}>
      <StyledChip
        cursor={isTruncated}
        label={<ChipLabel ref={textRef}>{label}</ChipLabel>}
        onMouseEnter={() => setIsOver(true)}
        onMouseLeave={() => setIsOver(false)}
        onClick={onClick}
        variant="outlined"
      />
    </Tooltip>
  )
}

const Container = styled('div')`
  display: block;
  overflow: hidden;
  width: 100%;
`

const ItemWrapper = styled('div')`
  display: flex;
  overflow: hidden;
`

const StyledOverflowList = styled(OverflowList)`
  gap: 4px;
  align-items: center;
` as typeof OverflowList

const renderItems = (items: string[]) => {
  return items.map((v) => {
    return (
      <ItemWrapper key={v}>
        <OverflowChip label={v} />
      </ItemWrapper>
    )
  })
}

const renderOverflow = (allItems: string[]) => {
  const OverflowRenderer = (items: string[]) => {
    return (
      items.length > 0 && (
        <Tooltip
          title={
            <UnorderedList>
              {allItems.map((item) => (
                <ListItem key={item}>{item}</ListItem>
              ))}
            </UnorderedList>
          }
        >
          <More variant="body1">{`+${items.length}`}</More>
        </Tooltip>
      )
    )
  }

  OverflowRenderer.displayName = 'OverflowRenderer'

  return OverflowRenderer
}

function parseValue(value: FieldValue<unknown> | null) {
  if (!value || typeof value?.value !== 'string') {
    return []
  }

  const str = value.value

  if (str.includes(';')) {
    return str.split(';')
  } else if (str.includes(',')) {
    return str.split(',')
  } else if (str.includes('|')) {
    return str.split('|')
  }

  return [str]
}

export function ChipList({ value }: CustomColumnProps) {
  const [ready, setReady] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => {
    timer.current = setTimeout(() => setReady(true), 0)

    return () => {
      clearTimeout(timer.current)
    }
  }, [])

  const values = parseValue(value)

  if (!values?.length) {
    return <Typography variant="body1">{EMPTY_CELL_VALUE}</Typography>
  }

  return (
    <Container>
      {ready && (
        <StyledOverflowList
          itemsRenderer={renderItems}
          overflowRenderer={renderOverflow(values)}
          items={values}
          minVisibleItems={1}
        />
      )}
    </Container>
  )
}
